import type { CitationSpan, Constituent } from '../types';

/**
 * Find numeric citation spans — `[1]`, `(1, 3)`, `¹⁻³` — in plain text.
 *
 * One span per token, one constituent per ordinal it names; a range is expanded and every
 * member shares the range token's offsets. This layer only reads shapes; whether ordinal 7
 * exists is for the ordinal index to say.
 *
 * Superscript convention: the text is expected to carry superscript markers as Unicode
 * superscript characters. A consumer whose superscripts are formatting marks over plain digits
 * builds the text of one block by passing every superscript-marked run through
 * `toSuperscript()` and turning every non-text inline node into a space, then hands that
 * string in. Two superscript runs joined by a comma (with any whitespace) form one span, as
 * `[1, 2]` would; whitespace alone does not join them.
 *
 * What is NOT a citation, and is pinned by fixtures:
 *   - bracket: an ordinal list containing `0`; a decimal (`[1.2, 3.4]`); an index directly on a
 *     lone symbol (`A[1]`, `x1[2]`) — a word may carry the bracket (`shown[1]`);
 *   - paren: a four-digit year (`(2020)`); a token after a number, a lone symbol or an operator
 *     (`Figure 1 (3)`, `p < .05 (2)`, `χ²(3)`, `E = mc² (1)`); a token that opens a line, which
 *     is a list marker;
 *   - superscript: an exponent on a lone symbol (`R²`, `m²`, `χ²`) or on a number of one or two
 *     digits, or any signed exponent (`10⁻³`, `1000⁻³`); a marker after a four-digit year is
 *     kept (`2020¹⁴`).
 * Honest limits: `(1)` at the end of a prose line is read as a citation even when it is an
 * equation number, `(1) … (2)` prose enumerations are read as citations, and a superscript
 * marker right after a one- or two-digit numeral (`Table 2¹`) is dropped as an exponent.
 */

export const NUMERIC_VARIANTS = ['bracket', 'paren', 'superscript'] as const;
export type NumericVariant = (typeof NUMERIC_VARIANTS)[number];

const LABELS: Record<NumericVariant, string> = { bracket: '[N]', paren: '(N)', superscript: '¹²³' };

/** How a numeric citation is written in the body for a variant — for consumer messages. */
export function numericVariantLabel(variant: NumericVariant): string {
  return LABELS[variant];
}

const SUP_VALUE: Record<string, string> = {
  '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4',
  '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9', '⁻': '-',
};
const PLAIN_TO_SUP: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻', '–': '⁻',
};

/** Digits and dashes to their Unicode superscript forms; every other character passes through. */
export function toSuperscript(s: string): string {
  let out = '';
  for (const ch of s) out += PLAIN_TO_SUP[ch] ?? ch;
  return out;
}

/** The reverse of `toSuperscript()`. */
export function fromSuperscript(s: string): string {
  let out = '';
  for (const ch of s) out += SUP_VALUE[ch] ?? ch;
  return out;
}

const SD = String.raw`[⁰¹²³⁴-⁹]`;
const SUP_NUMBER = String.raw`${SD}+(?:⁻${SD}+)?`;
const SUPERSCRIPT_RE = new RegExp(String.raw`${SUP_NUMBER}(?:\s*[,˒⸴]\s*${SUP_NUMBER})*`, 'gu');
const BRACKET_RE = /\[\s*(\d+(?:\s*[-–,;]\s*\d+)*)\s*\](?:\s*[-–]\s*\[\s*(\d+)\s*\])?/gu;
const PAREN_RE = /\(\s*(\d+(?:\s*[-–,;]\s*\d+)*)\s*\)/gu;

interface OrdinalToken {
  ordinal: number;
  /** Offsets into the text of the digits (a single number) or of the range token (a member). */
  start: number;
  end: number;
}

const MAX_RANGE = 100;

/** Expand `1, 3; 5-7` (already in plain digits) into ordinal tokens; null when it is not a
 *  citation list (a zero, a reversed or over-long range). */
function expandList(inner: string, innerStart: number): OrdinalToken[] | null {
  const out: OrdinalToken[] = [];
  const partRe = /(\d+)(?:\s*[-–]\s*(\d+))?/gu;
  let m: RegExpExecArray | null;
  while ((m = partRe.exec(inner)) !== null) {
    const a = parseInt(m[1], 10);
    const start = innerStart + m.index;
    const end = start + m[0].length;
    if (m[2] !== undefined) {
      const b = parseInt(m[2], 10);
      if (a === 0 || b < a || b - a >= MAX_RANGE) return null;
      for (let i = a; i <= b; i++) out.push({ ordinal: i, start, end });
    } else {
      if (a === 0) return null;
      out.push({ ordinal: a, start, end });
    }
  }
  return out.length > 0 ? out : null;
}

function constituentsFor(text: string, tokens: OrdinalToken[]): Constituent[] {
  return tokens.map((t) => ({
    start: t.start,
    end: t.end,
    text: text.slice(t.start, t.end),
    names: [],
    nameOffsets: [],
    particles: [],
    year: '',
    yearSuffix: '',
    etAl: false,
    arity: 0,
    ordinal: t.ordinal,
  }));
}

function span(text: string, start: number, end: number, tokens: OrdinalToken[]): CitationSpan {
  return { start, end, text: text.slice(start, end), kind: 'numeric', constituents: constituentsFor(text, tokens) };
}

/** `(?:^|[^\p{L}])\p{L}$` — the text ends in a letter standing alone: a symbol, not a word. */
function endsInLoneSymbol(before: string): boolean {
  return /(?:^|[^\p{L}])\p{L}$/u.test(before);
}

function endsInDigit(before: string): boolean {
  return /[\d⁰¹²³⁴-⁹]$/u.test(before);
}

function bracketSpans(text: string): CitationSpan[] {
  const spans: CitationSpan[] = [];
  BRACKET_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = BRACKET_RE.exec(text)) !== null) {
    const before = text.slice(0, m.index);
    // An index on a lone symbol or a number — `A[1]`, `x1[2]` — is notation; `shown[1]` is a
    // word carrying its marker and is kept.
    if (/\S$/u.test(before) && (endsInLoneSymbol(before) || /\d$/u.test(before))) continue;
    const innerStart = m.index + m[0].indexOf(m[1]);
    let tokens: OrdinalToken[] | null;
    if (m[2] !== undefined) {
      // `[1]–[3]`: a range written as two tokens.
      if (!/^\d+$/u.test(m[1])) continue;
      const a = parseInt(m[1], 10);
      const b = parseInt(m[2], 10);
      if (a === 0 || b < a || b - a >= MAX_RANGE) continue;
      tokens = [];
      for (let i = a; i <= b; i++) tokens.push({ ordinal: i, start: m.index, end: m.index + m[0].length });
    } else {
      tokens = expandList(m[1], innerStart);
    }
    if (!tokens) continue;
    spans.push(span(text, m.index, m.index + m[0].length, tokens));
  }
  return spans;
}

const YEAR_LIKE_RE = /^(?:1[5-9]|20)\d{2}$/u;

function parenSpans(text: string): CitationSpan[] {
  const spans: CitationSpan[] = [];
  PAREN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PAREN_RE.exec(text)) !== null) {
    const numbers = m[1].split(/\s*[-–,;]\s*/u);
    if (numbers.some((n) => YEAR_LIKE_RE.test(n))) continue;
    const raw = text.slice(0, m.index);
    // A token opening a line is a list marker.
    if (/(?:^|\n)[ \t]*$/u.test(raw)) continue;
    const before = raw.trimEnd();
    // After a number, a lone symbol or an operator the token is a statistic, a figure part or
    // an equation number, not a citation.
    if (endsInDigit(before) || endsInLoneSymbol(before) || /[=+\-−×÷/*^_<>±%]$/u.test(before)) continue;
    const tokens = expandList(m[1], m.index + m[0].indexOf(m[1]));
    if (!tokens) continue;
    spans.push(span(text, m.index, m.index + m[0].length, tokens));
  }
  return spans;
}

/** A superscript run is notation when it hangs off a lone symbol or a short or signed number. */
function superscriptIsNotation(raw: string): boolean {
  const before = raw.replace(/[⁺-⁾]+$/u, '');
  const hadSign = before.length !== raw.length;
  const base = /(\d+)$/u.exec(before);
  if (base && (hadSign || base[1].length <= 2)) return true;
  return endsInLoneSymbol(before);
}

function superscriptSpans(text: string): CitationSpan[] {
  const spans: CitationSpan[] = [];
  SUPERSCRIPT_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = SUPERSCRIPT_RE.exec(text)) !== null) {
    if (superscriptIsNotation(text.slice(0, m.index))) continue;
    // Decode in place so offsets stay those of the superscript characters (all are one UTF-16
    // unit long, as are the plain digits they map to).
    const tokens = expandList(fromSuperscript(m[0]).replace(/[˒⸴]/gu, ','), m.index);
    if (!tokens) continue;
    spans.push(span(text, m.index, m.index + m[0].length, tokens));
  }
  return spans;
}

/** Every numeric citation span of one variant, in text order. */
export function findNumericSpans(text: string, variant: NumericVariant): CitationSpan[] {
  switch (variant) {
    case 'bracket':
      return bracketSpans(text);
    case 'paren':
      return parenSpans(text);
    case 'superscript':
      return superscriptSpans(text);
  }
}
